/*
 * Copyright (c) 2024. Tooling Team
 */
//todo: integrate this file with US990243 changes once available

import PropTypes from "prop-types";
import { ModalDialog } from "@uhg-abyss/web/ui/ModalDialog";
import { Layout } from "@uhg-abyss/web/ui/Layout";
import { Button } from "@uhg-abyss/web/ui/Button";
import { LoadingOverlay } from "@uhg-abyss/web/ui/LoadingOverlay";
import { Text } from "@uhg-abyss/web/ui/Text";
import { DataTable } from "@uhg-abyss/web/ui/DataTable";
import { useDataTable } from "@uhg-abyss/web/hooks/useDataTable";
import { useEffect, useMemo, useState } from "react";
import {
  downloadInvalidRows,
  extractRowObject,
  getFailedRecordCount,
  getFormattedRows,
  getValidationMsg,
  isDownloadDisabled,
} from "./BulkUploadUtils.js";
import { Grid } from "@uhg-abyss/web/ui/Grid";

const PREVIEW_ROW_LIMIT = 5;

const formatPreviewCellValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (Array.isArray(value)) {
    return value.map(formatPreviewCellValue).join(", ");
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
};

const tagsAreAllEmpty = (rows) => {
  if (!rows || rows.length === 0) {
    return true;
  }

  return rows.every((row) => !row.tags || row.tags.length === 0);
};

const getPreviewColumns = (rows) => {
  if (!rows || rows.length === 0) {
    return [];
  }

  const shouldHideTags = tagsAreAllEmpty(rows);

  return Object.keys(rows[0])
    .filter((column) => !(column === "tags" && shouldHideTags))
    .reverse()
    .map((column) => ({
      id: column,
      accessorKey: column,
      header: column,
      cell: (cell) => formatPreviewCellValue(cell.getValue()),
      minSize: 140,
    }));
};

export default function BulkUploadPreviewForm(props) {
  const { formCallback } = props;
  const [isDisabled, setDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const failedRowData = useMemo(() => getFormattedRows(props), [props]);
  const failedPreviewRows = useMemo(
    () => failedRowData.slice(0, PREVIEW_ROW_LIMIT),
    [failedRowData]
  );
  const succeededPreviewRows = useMemo(
    () =>
      (props.formData.succeededRows || [])
        .map((row) => extractRowObject(row))
        .slice(0, PREVIEW_ROW_LIMIT),
    [props.formData.succeededRows]
  );
  const previewColumns = useMemo(
    () =>
      getPreviewColumns(
        failedPreviewRows.length > 0 ? failedPreviewRows : succeededPreviewRows
      ),
    [failedPreviewRows, succeededPreviewRows]
  );

  const failedTableProps = useDataTable({
    initialData: failedPreviewRows,
    initialColumns: previewColumns,
    tableConfig: {
      enableColumnResizing: true,
    },
    defaultSettingsConfig: {
      rowHeight: "compact",
    },
  });
  const succeededTableProps = useDataTable({
    initialData: succeededPreviewRows,
    initialColumns: previewColumns,
    tableConfig: {
      enableColumnResizing: true,
    },
    defaultSettingsConfig: {
      rowHeight: "compact",
    },
  });

  useEffect(() => {
    failedTableProps.setData(failedPreviewRows, true);
  }, [failedPreviewRows, failedTableProps.setData]);

  useEffect(() => {
    failedTableProps.setColumns(previewColumns, true);
    succeededTableProps.setColumns(previewColumns, true);
  }, [
    failedTableProps.setColumns,
    previewColumns,
    succeededTableProps.setColumns,
  ]);

  useEffect(() => {
    succeededTableProps.setData(succeededPreviewRows, true);
  }, [succeededPreviewRows, succeededTableProps.setData]);

  const upload = () => {
    setIsLoading(true);
    setDisabled(true);
    formCallback(props.formData.file);
  };

  return (
    <div>
      <ModalDialog
        isOpen={props.isOpen}
        header={"Bulk Upload Preview of " + props.formData.fileName}
        onClose={props.onClose}
        footer={
          <Layout.Group alignLayout="right">
            <Button
              isDisabled={
                props.formData.succeededRows.length === 0 || isDisabled
              }
              onClick={upload}
              isLoading={isLoading}
              ariaLoadingLabel="Uploading records"
            >
              Upload
            </Button>
            <Button
              onClick={props.onClose}
              isDisabled={isDisabled}
              variant="outline"
              color="neutral"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              color="neutral"
              isDisabled={isDownloadDisabled(props)}
              onClick={() => downloadInvalidRows(props, failedRowData)}
            >
              Download Invalid Rows
            </Button>
          </Layout.Group>
        }
      >
        {isLoading ? (
          <div className="m-5 dialog-loading">
            <LoadingOverlay
              isLoading={true}
              loadingTitle="Loading..."
              loadingMessage="Please wait..."
              ariaLoadingLabel="Please wait..."
            />
          </div>
        ) : (
          <Grid>
            <Grid.Col span={12}>
              <Text fontSize="xs">
                Note: Only up to first five records shown for each table.
              </Text>
              <br />
              {getValidationMsg(props, "validated")}
              <br />
              Failed Preview
              <div id="failedGrid" data-testid="failed-preview-table">
                <DataTable
                  title="Failed Preview"
                  hideHeader
                  tableState={failedTableProps}
                >
                  <DataTable.Table hiddenColumnMode="remove" scrollableFocus />
                </DataTable>
              </div>
              <Text fontSize="xs">
                Total number of failed records: {getFailedRecordCount(props)}
              </Text>
              <br />
              Succeeded Preview
              <div id="succeededGrid" data-testid="succeeded-preview-table">
                <DataTable
                  title="Succeeded Preview"
                  hideHeader
                  tableState={succeededTableProps}
                >
                  <DataTable.Table hiddenColumnMode="remove" scrollableFocus />
                </DataTable>
              </div>
              <Text fontSize="xs">
                Total number of succeeded records: {}
                {props.formData.succeededRows.length}
              </Text>
            </Grid.Col>
          </Grid>
        )}
      </ModalDialog>
    </div>
  );
}

BulkUploadPreviewForm.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  bulkUploadPreviewCallback: PropTypes.func,
  formData: PropTypes.object,
  formCallback: PropTypes.func,
  activeDomain: PropTypes.object,
};
